import { MultilineInput } from '@amelio/ui-library';

import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { showErrorToast } from '@/plugins/Toast';

export type TranslationRowStyle = {
    /** Indentation of the source text, so a list can show what belongs under what. */
    indent?: string;
    /** Styling of the source text itself. */
    text?: string;
};

interface TranslationRowProps {
    sourceText: string;
    targetValue: string;
    onTargetChange: (value: string) => void;
    /** Refused beyond this many characters; unbounded when left out. */
    maxLength?: number;
    /** Shown before the source text, for a list that marks what kind of line this is. */
    marker?: ReactNode;
    sourceStyle?: TranslationRowStyle;
}

/**
 * One line of a translation panel: the wording as it reads today on the left, the box it is being
 * translated into on the right, and a dot that says whether that language has been written yet.
 *
 * Shared by every screen that translates its own content, so the panels stay the same to read and
 * to use; what differs between them — indentation, markers, length limits — arrives as props.
 */
export const TranslationRow = ({
    sourceText,
    targetValue,
    onTargetChange,
    maxLength,
    marker,
    sourceStyle,
}: TranslationRowProps) => {
    const { t } = useAmelioTranslations();
    const isTranslated = targetValue.trim().length > 0;

    const handleChange = (value: string) => {
        if (maxLength !== undefined && value.length > maxLength) {
            showErrorToast({
                message: t('common.translation.maxLengthExceeded', { max: maxLength }),
            });
            onTargetChange(value.slice(0, maxLength));
            return;
        }

        onTargetChange(value);
    };

    return (
        <div
            className={
                'flex flex-col gap-2 border-b border-gray-200 py-3 font-normal md:flex-row md:items-center md:gap-6'
            }
        >
            <div className={`flex min-w-0 flex-1 items-center gap-2 ${sourceStyle?.indent ?? ''}`}>
                {marker}

                <span className={`${sourceStyle?.text ?? ''} max-w-full wrap-break-word`}>
                    {sourceText || (
                        <span className={'text-base-300 text-md italic'}>
                            {t('common.translation.emptySource')}
                        </span>
                    )}
                </span>
            </div>

            <div className={'flex min-w-0 flex-1 items-center gap-3'}>
                <span
                    className={`h-2 w-2 flex-shrink-0 rounded-full ${
                        isTranslated ? 'bg-success' : 'bg-base-200'
                    }`}
                />

                <div className={'flex-1'}>
                    <MultilineInput
                        value={targetValue}
                        onChange={handleChange}
                        placeholder={t('common.translation.placeholder')}
                        variant={'white'}
                        autoGrow
                        rows={1}
                        className={'text-md border-gray-200 font-normal'}
                    />
                </div>
            </div>
        </div>
    );
};
